/**
 * components/riskTerminal/StatusStrip.jsx
 *
 * upstox-074 (handoff items 01 and 04) — the status strip's left and right ends. Left: ONE segmented
 * account switch (the client ID under the active account), a divider, the trend (the selected method's
 * direction headline and a two-row radio list: method in sentence case = the control, reading in CAPS =
 * the market). Right: freshness in one place. Nothing here changes what any control sends: the
 * account switch, the method switch and the sign-in link are the screen's own callbacks.
 */

import React from 'react';
import { C, FONT } from './theme';
import { accountName, accountColor } from './accounts';
import { Tip } from './hoverCard';
import { agoText } from './model';

const SEG_BORDER = '#cfc9bd';

/** Upstox's client id or "not signed in"; a Kite account's Zerodha id. */
export function clientIdOf(account, status) {
  if (!status) return '';
  const a = (status && status.account) || {};
  if (account && account.broker === 'KITE') return a.zerodha_id || '';
  return a.upstox_user_id || 'not signed in';
}

export function AccountSegments({ accounts, account, status, onSwitch }) {
  if (!accounts || !account) return null;
  return (
    <div data-account-switch role="group" aria-label="Account" style={{ display: 'flex', alignItems: 'stretch', gap: '2px', padding: '2px', border: '1px solid ' + SEG_BORDER, background: '#fff' }}>
      {accounts.map(a => {
        const col = accountColor(accounts, a);
        const on = a.id === account.id && a.broker === account.broker;
        const id = on ? clientIdOf(a, status) : '';
        return (
          <button key={a.id == null ? 'own' : a.id} type="button" className={on ? undefined : 'rt-seg'} data-account={accountName(a)} data-active={on ? 'true' : undefined}
            aria-pressed={on} onClick={() => !on && onSwitch && onSwitch(a)} style={{ '--seg-tint': col.tint,
              minWidth: '84px', height: '36px', padding: '0 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1px',
              border: 'none', borderRadius: 0, fontFamily: FONT, cursor: on ? 'default' : 'pointer', background: on ? col.ink : 'transparent' }}>
            <span style={{ fontSize: '10.5px', letterSpacing: '0.1em', fontWeight: on ? 700 : 600, color: on ? '#fff' : C.ink3, whiteSpace: 'nowrap' }}>{accountName(a)}</span>
            {on && id && <span data-client-id style={{ fontSize: '11px', letterSpacing: '0.04em', color: col.id, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{id}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** The radio list's keyboard: arrow keys move between the two rows. */
function radioKey(e, rows, pick) {
  if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
  e.preventDefault();
  const i = rows.findIndex(r => r.selected);
  const next = rows[(i + (e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? rows.length - 1 : 1)) % rows.length];
  pick(next.method);
  const el = e.currentTarget.parentElement && e.currentTarget.parentElement.querySelector(`[data-method="${next.method}"]`);
  if (el) el.focus();
}

export function TrendBlock({ trend, strip, note, busy, onPick, shared, lastSwitch, children }) {
  const switchTip = shared && shared.length
    ? 'Shared with ' + shared.join(', ') + ' on this account · ' + (lastSwitch ? 'last switched by ' + lastSwitch.by + ' ' + hhmmOf(lastSwitch.at) : 'not switched yet')
    : '';
  return (
    <div data-trend style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '14px', padding: '6px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '30px', height: '4px', flex: '0 0 30px', background: strip.solid ? C.ink : strip.known ? 'repeating-linear-gradient(90deg,#1a1917 0 4px,transparent 4px 8px)' : 'repeating-linear-gradient(90deg,#aba396 0 4px,transparent 4px 8px)', opacity: strip.known ? 1 : 0.7 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
          <Tip dotted text={trend.sub} head="TREND" as="div" data-trend-headline style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.08em', color: strip.known ? C.ink : C.ink5 }}>{strip.headline}</Tip>
          {note && <div data-trend-note style={{ fontSize: '10px', color: C.amber, fontWeight: 600, letterSpacing: '0.04em' }}>{note}</div>}
        </div>
      </div>
      <div role="radiogroup" aria-label="Trend method" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {strip.rows.map(r => (
          <div key={r.method} role="radio" aria-checked={r.selected} tabIndex={r.selected ? 0 : -1} data-method={r.method} data-selected={r.selected ? 'true' : undefined}
            onClick={() => !r.selected && !busy && onPick(r.method)} onKeyDown={e => radioKey(e, strip.rows, m => !busy && onPick(m))}
            data-tip={r.method === 'AGGRESSIVE' ? 'K1 vs E9 — against it is red: timers and the interrupt run' : 'K1 vs K5 and E9 — trend or consolidation'}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: r.selected ? 'default' : 'pointer', outline: 'none' }}>
            <span aria-hidden="true" style={{ width: '10px', height: '10px', flex: '0 0 10px', borderRadius: '50%', border: '1.5px solid ' + (r.selected ? C.ink : C.ink5), boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {r.selected && <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: C.ink }} />}
            </span>
            <span style={{ width: '84px', flex: '0 0 84px', fontSize: '12.5px', letterSpacing: 0, fontWeight: r.selected ? 500 : 400, color: r.selected ? C.ink : C.ink5 }}>{r.name}</span>
            <span data-reading={r.method} style={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.1em', whiteSpace: 'nowrap',
              color: !r.ready ? C.ink5 : r.differs ? C.amber : C.ink }}>{r.words}</span>
          </div>
        ))}
      </div>
      {switchTip && <span data-shared-icon className="rt-tip-plain" data-tip={switchTip} data-tip-head="SHARED READING" aria-label="Shared reading" style={{ fontFamily: "'Material Symbols Outlined'", fontSize: '17px', color: C.ink5, lineHeight: 1, cursor: 'default' }}>group</span>}
      {children}
    </div>
  );
}

function hhmmOf(iso) {
  const m = /T(\d{2}:\d{2})/.exec(iso || '');
  return m ? m[1] : '';
}

/** Right end: the worst item + "+N" (all in its card), the quiet text when nothing is wrong, DATA <age> last. */
export function FreshnessEnd({ fresh, dataAgeMs, dataTip, onSignIn, signinUrl }) {
  const [first, ...rest] = fresh.items;
  const toneCol = t => (t === 'red' ? C.red : C.amber);
  const allTip = fresh.items.map(i => (i.name + ' · ' + i.text + (i.hover ? ' — ' + i.hover : ''))).join('\n');
  const chip = first && (
    <Tip as={first.signin ? 'a' : 'div'} text={allTip} head={rest.length ? 'EVERYTHING WRONG, WORST FIRST' : first.name} tone={first.tone} data-fault={first.name}
      {...(first.signin ? { href: signinUrl, target: '_blank', rel: 'noopener', onClick: onSignIn } : {})}
      style={{ display: 'flex', alignItems: 'baseline', gap: '7px', padding: '4px 9px', textDecoration: 'none', cursor: first.signin ? 'pointer' : 'default',
        background: first.tone === 'red' ? C.redTint : C.amberTint, border: '1px solid ' + toneCol(first.tone) }}>
      <span style={{ fontSize: '10px', color: toneCol(first.tone), letterSpacing: '0.1em', fontWeight: 700 }}>{first.name}</span>
      <span style={{ fontSize: '11px', color: C.ink, fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{first.text}</span>
      {first.signin && <span style={{ fontSize: '10px', color: C.red, letterSpacing: '0.1em', fontWeight: 700, textDecoration: 'underline' }}>SIGN IN</span>}
      {rest.length > 0 && <span data-fault-more style={{ fontSize: '10px', color: C.ink3, fontWeight: 700 }}>+{rest.length}</span>}
    </Tip>
  );
  return (
    <div data-freshness style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      {chip || <div data-session-note style={{ fontSize: '10px', color: C.ink4, letterSpacing: '0.1em' }}>{fresh.grey}</div>}
      <div style={{ width: '1px', alignSelf: 'stretch', background: C.line, margin: '8px 0' }} />
      <Tip as="div" text={dataTip} data-data-age style={{ fontSize: '10px', color: C.ink5, letterSpacing: '0.06em', fontVariantNumeric: 'tabular-nums' }}>
        {dataAgeMs != null ? 'DATA ' + agoText(dataAgeMs / 1000) : ''}
      </Tip>
    </div>
  );
}
